import type {ReactElement, CSSProperties} from 'react';
import React, {useState, useRef, useEffect} from 'react';
import styles from './ai-chat-inspiration-visual.module.scss';
import {InspirationLayoutVariant} from '@/ai-chat/components/ai-chat-window-inspirations/ai-chat-window-inspirations.types';
import {getAssetUrl} from '@Utils/s3.util';
import type {
  AiChatInspirationVisualProps,
  AnimationBounds,
} from '@/ai-chat/components/ai-chat-window-inspirations/components/ai-chat-inspiration/components/ai-chat-inspiration-visual/ai-chat-inspiration-visual.types';

const ANIMATION_DURATION = 1300;
const RESET_DELAY = 3000;
const REVERSE_ANIMATION_DURATION = ANIMATION_DURATION / 4;

export function AiChatInspirationVisual({
  imageURL,
  imageURLToConvertTo,
  layoutVariant,
  isHovered = false,
  isMobileView = false,
  imageClasses,
  isExpanded = false,
  attachmentURL = '',
  onAnimationStart,
}: AiChatInspirationVisualProps): ReactElement {
  const [animationProgress, setAnimationProgress] = useState(0.5);
  const [imageBounds, setImageBounds] = useState({left: 0, width: 0, containerWidth: 0});
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const resetTimeoutRef = useRef<number | null>(null);
  const reverseAnimationFrameRef = useRef<number | null>(null);
  const forwardAnimationFrameRef = useRef<number | null>(null);
  const hasAnimatedRef = useRef(false);
  const previousTriggerStateRef = useRef(false);
  const isAnimationRunningRef = useRef(false);
  const baseImageRef = useRef<HTMLImageElement>(null);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const clearAllTimeouts = (): void => {
    if (reverseAnimationFrameRef.current) {
      cancelAnimationFrame(reverseAnimationFrameRef.current);
      reverseAnimationFrameRef.current = null;
    }
    if (forwardAnimationFrameRef.current) {
      cancelAnimationFrame(forwardAnimationFrameRef.current);
      forwardAnimationFrameRef.current = null;
    }
  };

  const clearResetTimeout = (): void => {
    if (resetTimeoutRef.current) {
      clearTimeout(resetTimeoutRef.current);
      resetTimeoutRef.current = null;
    }
  };

  const getAnimationBounds = (): AnimationBounds => {
    if (imageBounds.containerWidth === 0) {
      return {startPercent: 0, endPercent: 100};
    }

    const startPercent = (imageBounds.left / imageBounds.containerWidth) * 100;
    const endPercent = ((imageBounds.left + imageBounds.width) / imageBounds.containerWidth) * 100;

    return {startPercent, endPercent};
  };

  const measureImageBounds = (): void => {
    if (!baseImageRef.current || !wrapperRef.current) {
      return;
    }

    const baseImageRect = baseImageRef.current.getBoundingClientRect();
    const wrapperRect = wrapperRef.current.getBoundingClientRect();

    const relativeLeft = baseImageRect.left - wrapperRect.left;
    const containerWidth = wrapperRect.width;

    setImageBounds({
      left: relativeLeft,
      width: baseImageRect.width,
      containerWidth,
    });
  };

  const startReverseAnimation = (): void => {
    resetTimeoutRef.current = window.setTimeout(() => {
      const startTime = Date.now();

      const animate = (): void => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / REVERSE_ANIMATION_DURATION, 1);
        setAnimationProgress(1 - progress * 0.5);

        if (progress < 1) {
          reverseAnimationFrameRef.current = requestAnimationFrame(animate);
        } else {
          hasAnimatedRef.current = false;
        }
      };

      animate();
    }, RESET_DELAY);
  };

  const runProgressAnimation = (): void => {
    const startTime = Date.now();
    onAnimationStart?.();
    isAnimationRunningRef.current = true;

    const animate = (): void => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / ANIMATION_DURATION, 1);
      setAnimationProgress(0.5 + progress * 0.5);

      if (progress < 1) {
        forwardAnimationFrameRef.current = requestAnimationFrame(animate);
      } else {
        isAnimationRunningRef.current = false;
        startReverseAnimation();
      }
    };

    animate();
  };

  const handleHoverChange = (): void => {
    const triggerCondition = isMobileView ? isExpanded : isHovered;
    const previousTriggerState = previousTriggerStateRef.current;
    previousTriggerStateRef.current = triggerCondition;

    if (previousTriggerState && !triggerCondition) {
      if (forwardAnimationFrameRef.current) {
        cancelAnimationFrame(forwardAnimationFrameRef.current);
        forwardAnimationFrameRef.current = null;
      }
      return;
    }

    const isNewTrigger = !previousTriggerState && triggerCondition;
    if (isNewTrigger) {
      hasAnimatedRef.current = false;
    }

    const shouldStartAnimation = triggerCondition && imageURLToConvertTo && !hasAnimatedRef.current;
    if (shouldStartAnimation) {
      clearAllTimeouts();
      clearResetTimeout();
      hasAnimatedRef.current = true;
      runProgressAnimation();
    }
  };

  useEffect(() => {
    return (): void => {
      clearAllTimeouts();
      clearResetTimeout();
    };
  }, []);

  useEffect(() => {
    const baseImage = baseImageRef.current;
    if (!baseImage) return;

    baseImage.addEventListener('load', measureImageBounds);
    measureImageBounds();

    const resizeObserver = new ResizeObserver(() => {
      measureImageBounds();
    });

    resizeObserver.observe(baseImage);

    return (): void => {
      baseImage.removeEventListener('load', measureImageBounds);
      resizeObserver.disconnect();
    };
  }, [layoutVariant, isMobileView, isExpanded]);

  useEffect(() => {
    handleHoverChange();
  }, [isHovered, imageURLToConvertTo, isExpanded, isMobileView]);

  const getLayoutClassName = (): string => {
    const layoutMap: Record<InspirationLayoutVariant, string> = {
      [InspirationLayoutVariant.BottomRightRadius]: styles.layoutBottomRight,
      [InspirationLayoutVariant.BottomLeftRadius]: styles.layoutBottomLeft,
      [InspirationLayoutVariant.TopRightRadius]: styles.layoutTopRight,
      [InspirationLayoutVariant.TopLeftRadius]: styles.layoutTopLeft,
      [InspirationLayoutVariant.Centered]: styles.layoutCentered,
      [InspirationLayoutVariant.FullWidth]: styles.layoutFullWidth,
    };

    return layoutMap[layoutVariant] || '';
  };

  const getConvertedImageStyle = (): CSSProperties => {
    const bounds = getAnimationBounds();
    const animationRange = bounds.endPercent - bounds.startPercent;
    const revealPixels = (animationProgress * animationRange * imageBounds.containerWidth) / 100;
    const rightInset = imageBounds.width - revealPixels;

    return {
      clipPath: `inset(0 ${rightInset}px 0 0)`,
    };
  };

  const getWandIconStyle = (): CSSProperties => {
    const bounds = getAnimationBounds();
    const animationRange = bounds.endPercent - bounds.startPercent;
    const wandPosition = bounds.startPercent + animationProgress * animationRange;

    return {
      left: `${wandPosition}%`,
      opacity: 1,
      transition: 'none',
    };
  };

  const handleImageLoad = (): void => {
    setIsImageLoaded(true);
  };

  const getAttachment = (): ReactElement | null => {
    if (!attachmentURL) {
      return null;
    }

    return <img ref={baseImageRef} src={getAssetUrl(attachmentURL)} alt="" className={styles.attachmentImage} />;
  };

  const renderConversionOverlay = (): ReactElement | null => {
    if (!imageURLToConvertTo) {
      return null;
    }

    return (
      <>
        <img src={getAssetUrl(imageURLToConvertTo)} alt="" className={`${styles.convertedImage}${imageClasses ? ` ${imageClasses}` : ''}`} style={getConvertedImageStyle()} />
        <img src={getAssetUrl('wand-progress-bar-line.webp')} alt="" className={styles.wandIcon} style={getWandIconStyle()} />
      </>
    );
  };

  return (
    <div className={`${styles.visualContainer} ${getLayoutClassName()} ${isMobileView ? styles.mobileView : ''} ${isExpanded ? styles.expandedView : ''}`}>
      {getAttachment()}

      <div className={`${styles.imageWrapper} ${isImageLoaded ? styles.imageLoaded : ''}`} ref={wrapperRef}>
        <img ref={baseImageRef} src={getAssetUrl(imageURL)} alt="" className={`${styles.baseImage}${imageClasses ? ` ${imageClasses}` : ''}`} onLoad={handleImageLoad} />
        {renderConversionOverlay()}
      </div>
    </div>
  );
}
